import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import HeroSection from './components/HeroSection';
import FeaturedSection from './components/FeaturedSection';
import CategorySection from './components/CategorySection';
import LatestSection from './components/LatestSection';

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '棒棒TV',
    url: 'https://www.bangbangtv.com/',
    description: '棒棒TV提供在线不缓冲秒播体验，即点即看零等待，海量免费高清视频流畅播放。电视剧全集、电影大全、综艺动漫等丰富内容。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.bangbangtv.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '棒棒TV',
    url: 'https://www.bangbangtv.com/',
    description: '中国领先的免费高清视频在线播放平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '棒棒TV真的完全免费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，棒棒TV所有视频内容均可免费观看，无需注册会员，无需支付任何费用。',
        },
      },
      {
        '@type': 'Question',
        name: '为什么棒棒TV可以实现秒播不缓冲？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '棒棒TV采用业界领先的CDN加速技术和智能分发系统，在全国范围内部署了数千个加速节点，实现零等待秒播体验。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '棒棒TV完美适配PC、手机、平板、智能电视等多种设备，在任何设备上畅享高清视频内容。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="首页"
        description="棒棒TV - 在线不缓冲秒播，即点即看零等待。免费高清视频流畅播放，海量电视剧全集、电影大全、综艺动漫，随时随地畅享视听盛宴。"
        keywords="在线不缓冲,秒播,即点即看,零等待,棒棒TV,免费高清视频,电视剧全集,电影大全,流畅播放,在线视频,免费看电影,免费追剧"
        canonicalUrl="https://www.bangbangtv.com/"
        ogType="website"
      />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <FeaturedSection />
          <CategorySection />
          <LatestSection />

          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  为什么选择棒棒TV？
                </h2>
                <p className="text-sm text-foreground-500 mt-1">四大核心优势，打造极致观影体验</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                  { icon: 'ri-flashlight-line', title: '秒播技术', desc: '先进的视频加速技术，实现真正的即点即看零等待，告别缓冲烦恼。' },
                  { icon: 'ri-4k-line', title: '高清画质', desc: '全面支持1080P和4K超高清播放，为您呈现每一帧的精彩细节。' },
                  { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配手机、平板、电脑等全终端设备，随时随地畅享精彩内容。' },
                  { icon: 'ri-vip-crown-line', title: '完全免费', desc: '所有内容均可免费观看，无需会员，无需付费，真正实现零门槛观看。' },
                ].map((item) => (
                  <div key={item.title} className="flex flex-col items-center text-center p-5 md:p-6">
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-primary-100 rounded-2xl mb-4">
                      <i className={`${item.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
                <h2 className="text-xl md:text-3xl font-bold text-white font-heading mb-3">
                  立刻开始您的观影之旅
                </h2>
                <p className="text-sm md:text-base text-white/80 mb-6 max-w-lg mx-auto">
                  海量高清视频资源，秒播零等待，完全免费。加入5000万用户的行列，畅享棒棒TV带来的极致体验！
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <a href="#featured" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap text-sm">
                    <i className="ri-play-circle-line text-lg"></i>
                    开始观看
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}